import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom"
import { BdaModel } from "../../models/BdaModel";
import { BdaDataset } from "../../models/BdaDataset";
import { Models } from "../../api/Models";
import { useToasts } from "../../contexts/toasts";
import { Breadcrumb, Button, Container, FormControl, Modal, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import AppLoader from "../../components/common/AppLoader";
import PolicyForm, { PolicyDTO } from "../../components/forms/PolicyForm";
import { Alert, DatePicker, Form, Input, Select, Steps } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { DataspaceConnector, Policy, PublishResourceDTO } from "../../models/DataSpace";
import IconButton from "../../components/common/IconButton";
import { DataRetriever } from "../../api/DataRetriever";
import classNames from "classnames";
import { v4 } from "uuid";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { Datasets } from "../../api/Datasets";
import { cloneDeep } from "lodash";

const formLayout = {
  labelCol: {
    xs: 24,
    md: 7,
  },
  wrapperCol: {
    xs: 24,
    md: 11,
  },
}

type Props = {
  resourceType: 'dataset' | 'model';
}

export default function PublishResource({ resourceType }: Props) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const [form] = Form.useForm();
  const [connectors, setConnectors] = useState<DataspaceConnector[]>();
  const [resource, setResource] = useState<BdaModel | BdaDataset | undefined>();
  const [error, setError] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [policies, setPolicies] = useState<Policy[]>();
  const [connectionError, setConnectionError] = useState(false);
  const [connectionDetails, setConnectionDetails] = useState<any>();
  const [selectedPolicies, setSelectedPolicies] = useState<PolicyDTO[]>([]);
  const [catalogues, setCatalogues] = useState<DefaultOptionType[]>();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<number>();
  const [editingPolicy, setEditingPolicy] = useState<PolicyDTO | undefined>();

  const dataspaceConnectorsOptions = useMemo(() => {
    return connectors?.map(conn => ({
      label: conn.name,
      value: conn.id
    }))
  }, [connectors]);
  const availablePolicies = useMemo(() => {
    return (policies || []).filter(policy => {
      return editingPolicy
        ? (policy.id === editingPolicy.policy || !selectedPolicies.some(p => p.policy === policy.id))
        : !selectedPolicies.some(p => p.policy === policy.id);
    });
  }, [selectedPolicies, policies, editingPolicy]);

  useEffect(() => {
    form.setFieldValue('policies', selectedPolicies);
  }, [selectedPolicies, form]);

  const initModel = useCallback(() => {
    Models.getModel(+id!)
      .then(
        ({ data: model }) => {
          setResource(model);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `Model Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [id, showToast]);

  const initDataset = useCallback(() => {
    Datasets.getDataset(+id!)
      .then(
        ({ data: dataset }) => {
          setResource(dataset);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `Dataset Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast]);

  useEffect(() => {
    if (resourceType === 'model') initModel();
    else initDataset();
  }, [initDataset, initModel, resourceType]);

  const getCatalogues = useCallback((values: any) => {
    setLoading(true);

    DataRetriever.getCatalogues(values.dataspaceConnector, values.user, values.password)
      .then(
        ({ data }) => {
          setLoading(false);
          setCatalogues(data.map((catalog) => ({ label: catalog, value: catalog })));
          setCurrentStep(1);
        },
        () => {
          setLoading(false);
          setConnectionError(true);
        }
      )
  }, []);

  const getConnectors = useCallback(() => {
    DataRetriever.getDataspaceConnectors()
      .then(
        ({ data }) => setConnectors(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('data-space-connectors-error', message, `Dataspace Connectors Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(() => getConnectors(), [getConnectors]);

  const handleCancel = useCallback(() => setCurrentStep(0), []);
  const openEditModal = useCallback(() => setShowModal(true), []);
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setEditing(undefined);
    setEditingPolicy(undefined);
  }, []);

  const handleFirstStepSubmit = useCallback((values: any) => {
    setConnectionError(false);
    setConnectionDetails(values);
    getCatalogues(values);
  }, [getCatalogues]);

  useEffect(() => {
    if (currentStep === 1 && !policies) {
      DataRetriever.getPolicies()
        .then(
          ({ data }) => setPolicies(data),
          (err) => {
            setPolicies([]);
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast('policies-error', message, `Policies Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }, [currentStep, policies, showToast]);

  const handlePublishResource = useCallback((values: any) => {
    const finalPolicies = selectedPolicies.map(policy => {
      const policyValues = policy.fields.reduce((acc, curr) => {
        const fieldValue = (typeof curr.value === 'object' && curr.value.hour)
          ? curr.value.hour(23).toISOString()
          : curr.value;
        acc[curr.key] = fieldValue;
        return acc;
      }, {} as Record<string, any>);

      return { policyId: policy.policy, policyValues };
    });

    const payload: PublishResourceDTO = {
      name: resource!.name,
      description: resource!.description,
      alidaUrl: window.location.href.replace('/publish', ''),
      datasource: resource!.datasource!,
      policies: finalPolicies,
      contractDate: values.contractDate.hour(23).toISOString(),
      contractStart: values.contractStart.hour(23).toISOString(),
    };
    showLoader();

    DataRetriever.publishResource(connectionDetails.dataspaceConnector, payload, connectionDetails.user, connectionDetails.password, values.catalog)
      .then(
        () => {
          hideLoader();
          navigate('/models');
          showToast('model-publish-success', 'Model published successfully.', 'Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `Model Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [resource, connectionDetails, navigate, showLoader, hideLoader, showToast, selectedPolicies]);

  const handleSubmit = useCallback((values: any) => {
    if (currentStep === 0) handleFirstStepSubmit(values);
    else handlePublishResource(values);
  }, [handleFirstStepSubmit, currentStep, handlePublishResource]);

  const handleRemovePolicy = useCallback((index: number) => () => {
    setSelectedPolicies(curr => {
      const newPolicies = [...curr];
      newPolicies.splice(index, 1);
      return newPolicies;
    });
  }, []);

  const handleEditPolicy = useCallback((index: number) => () => {
    const editingPolicy = selectedPolicies[index];
    const policy = policies?.find((policy) => policy.id === editingPolicy.policy)!;
    setEditingPolicy({ ...editingPolicy, policy: policy.id });
    setEditing(index);
    setShowModal(true);
  }, [selectedPolicies, policies]);

  const handlePolicySubmit = useCallback((values: PolicyDTO) => {
    if (editing !== undefined) {
      setSelectedPolicies(previous => {
        const newPolicies = cloneDeep(previous);
        newPolicies[editing] = { ...editingPolicy!, ...values };
        return newPolicies;
      });
    }
    else setSelectedPolicies(prevPolicies => prevPolicies.concat({ ...values, id: v4() }));
    handleModalClose();
  }, [editingPolicy, editing, handleModalClose]);

  const renderSecondStep = useCallback(() => {
    return (
      <>
        <Form.Item
          label="Catalog"
          name="catalog"
          rules={[{
            required: true,
            message: 'Catalog is required.'
          }]}
        >
          <Select
            className="w-100"
            placeholder="Catalog"
            options={catalogues}
            loading={!catalogues}
          />
        </Form.Item>

        <Form.Item
          name="contractStart"
          label="Contract Start"
          className="mt-4"
          rules={[{
            required: true,
            message: `Contract Start field is required.`
          }]}
        >
          <DatePicker className="w-100" />
        </Form.Item>

        <Form.Item
          name="contractDate"
          label="Contract Date"
          className="mt-4"
          rules={[{
            required: true,
            message: `Contract Date field is required.`
          }]}
        >
          <DatePicker className="w-100" />
        </Form.Item>

        <Form.Item
          name="policies"
          colon={false}
          rules={[
            {
              required: true,
              message: "You must choose at least one Policy"
            }
          ]}
          label={
            <>
              <span className="me-2">Policies</span>
              {selectedPolicies.length > 0 && (
                <IconButton type="button" tooltip="Add Policy" className="p-0" onClick={openEditModal}>
                  <FontAwesomeIcon icon={solid('circle-plus')} />
                </IconButton>
              )}
            </>
          }
        >
          {selectedPolicies.length === 0 ? (
            <Alert
              type="info"
              showIcon
              message="No policies added yet."
              action={
                <IconButton className="text-primary p-1" type="button" onClick={openEditModal}>
                  <FontAwesomeIcon icon={solid('plus')} />
                  <b className="ms-2">Add Policy</b>
                </IconButton>
              }
            />
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {selectedPolicies?.map((policy, index) => (
                <div className="mb-2 d-flex p-2 gap-2 align-items-center border rounded" key={policy.id}>
                  <b className="text-capitalize text-secondary me-2">{policies?.find((p) => p.id === policy.policy)?.name}</b>
                  <div className="d-flex flex-row">
                    <IconButton onClick={handleEditPolicy(index)} type="button" tooltip="Edit" className="p-1">
                      <FontAwesomeIcon icon={solid('pen')} />
                    </IconButton>
                    <IconButton onClick={handleRemovePolicy(index)} type="button" tooltip="Remove" className="text-danger p-1 ms-1">
                      <FontAwesomeIcon size="lg" icon={solid('times')} />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Form.Item>
      </>
    )
  }, [catalogues, openEditModal, handleEditPolicy, handleRemovePolicy, policies, selectedPolicies]);

  const renderFirstStep = useCallback(() => {
    return (
      <>
        <Form.Item label="Connector" name="dataspaceConnector" rules={[{ required: true, message: 'Data Space connector is required.' }]}>
          <Select className="w-100" placeholder="Connector" options={dataspaceConnectorsOptions} />
        </Form.Item>
        <Form.Item className="mt-4" label="User" name="user" rules={[{ required: true, message: 'User is required.' }]}>
          <Input placeholder="User" type="text" />
        </Form.Item>
        <Form.Item className="mt-4" label="Password" name="password" rules={[{ required: true, message: 'Password is required.' }]}>
          <Input.Password placeholder="Password" />
        </Form.Item>

        {connectionError && <FormControl.Feedback type="invalid" className="d-block px-2 text-center">An error occured while testing connection, try again later.</FormControl.Feedback>}
      </>
    )
  }, [dataspaceConnectorsOptions, connectionError]);

  const renderAsyncContent = () => {
    if (error) return (
      <Alert type="error" message={
        <>
          <FontAwesomeIcon icon={solid('exclamation-triangle')} />
          <span className="ms-2">An error occured. Please, try again later.</span>
        </>
      } />
    );

    if (!resource) return (
      <div className="d-flex justify-content-center py-5">
        <AppLoader size={300} />
      </div>
    );

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/${resourceType}s` }}>{resourceType === 'model' ? 'Models' : 'Datasets'}</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/${resourceType}s/${id}` }}>{resource.name}</Breadcrumb.Item>
          <Breadcrumb.Item active>Publish</Breadcrumb.Item>
        </Breadcrumb>

        <Form className="card" form={form} {...formLayout} onFinish={handleSubmit}>
          <div className="pt-4">
            <Steps current={currentStep} labelPlacement="vertical" size="small">
              <Steps.Step title="Connection Settings" />
              <Steps.Step title="Publish" />
            </Steps>
          </div>

          <div className="mt-3 px-3">
            {currentStep === 0 && renderFirstStep()}
            {currentStep === 1 && renderSecondStep()}
          </div>

          <div className="d-flex px-3 pb-3 justify-content-between">
            {currentStep === 1 && (
              <Button variant="light" size="sm" className="border" type="button" onClick={handleCancel}>
                <FontAwesomeIcon icon={solid('chevron-left')} />
                <span className="ms-2">Back</span>
              </Button>
            )}
            <Button type="submit" className={classNames({ 'ms-auto border': currentStep === 0 })} variant={currentStep === 1 ? "success" : "light"} size="sm" disabled={loading}>
              {loading
                ? <Spinner size="sm" />
                : <FontAwesomeIcon icon={currentStep === 0 ? solid('chevron-right') : regular('check-circle')} />
              }
              <span className="ms-2">{currentStep === 0 ? 'Next' : 'Publish'}</span>
            </Button>
          </div>
        </Form>

        <Modal show={showModal} onHide={handleModalClose}>
          <Modal.Header closeButton>
            <Modal.Title>{editingPolicy ? 'Edit' : 'Create'} Policy</Modal.Title>
          </Modal.Header>
          <PolicyForm policy={editingPolicy} availablePolicies={availablePolicies} onCancel={handleModalClose} onSubmit={handlePolicySubmit} />
        </Modal>
      </>
    )
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>Publish {resourceType === 'model' ? 'Model' : 'Dataset'} {id}</title>
      </Helmet>

      {renderAsyncContent()}
    </Container>
  )
}